// Top-mounted CRT scope strip. Slides into view only while the engine
// is transmitting; collapses out of layout entirely when stopped, so
// the editor reclaims the pixel budget at idle.
//
// Replaces the old bottom LiveDeck. The redundant readouts (cycle
// phasor / cycle counter / BPM) lived in both LiveDeck and TransportDeck
// — those stay in TransportDeck. The unique chassis bits (master-bus
// spectrum + sync-offset calibration) move into the slim left rail of
// this strip.
//
// Sizing: each voice rail is 28px (vs 44px on the old chassis), so the
// 1–8-voice common case fits without any scrolling. Beyond 8, the
// inner panel scrolls vertically — the strip itself never grows past
// its `--perf-strip-max` budget, so the editor below stays anchored.
//
// Reveal animation: a `grid-template-rows: 0fr → 1fr` transition is the
// only clean way to animate auto-height in CSS without hard-coding
// pixel max-heights or fighting `display:none`. The CRT warm-up uses an
// opacity ramp + a yellow tracer pulse on the bottom edge.
//
// Perf: same shared-ticker discipline as the old LiveDeck. The
// component re-renders only when `voiceOrder` changes — frame-rate hap
// updates flow through refs and never trigger React reconciliation.

import type { MutableRefObject } from 'react';
import type { HapRefs, HapStructuralFrame } from '../strudel';
import { useStartedState } from '../strudel';
import { Spectrum } from './visualizer/Spectrum';
import { VoiceTrace } from './visualizer/VoiceTrace';
import { SyncOffset } from './visualizer/SyncOffset';

const LOOKBEHIND = 1;
const LOOKAHEAD = 2;
const TRACE_HEIGHT = 28;

type Props = {
  refs: HapRefs;
  structural: HapStructuralFrame;
  nowRef: MutableRefObject<number>;
  syncOffsetMs: number;
  onSyncChange: (n: number) => void;
};

export function PerformanceStrip({
  refs,
  structural,
  nowRef,
  syncOffsetMs,
  onSyncChange,
}: Props) {
  const voiceOrder = structural.voiceOrder;
  const active = useStartedState(refs.started);
  const hasContent = voiceOrder.length > 0;

  return (
    <div
      data-active={active || undefined}
      aria-hidden={!active}
      className="perf-strip-wrap"
    >
      <section
        aria-label="Performance strip"
        className="perf-strip-inner relative flex items-stretch border-b border-zinc-900 bg-zinc-950 overflow-hidden"
      >
        {/* SVG defs — voice flare blur + the now-line glow gradient. The
            old LiveDeck declared identical defs; voice traces look up
            #voice-glow / #nowGlow by id so we re-declare them at this
            mount point now that LiveDeck is gone. */}
        <svg
          width="0"
          height="0"
          className="absolute pointer-events-none"
          aria-hidden="true"
        >
          <defs>
            <filter id="voice-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" />
            </filter>
            <linearGradient id="nowGlow" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="#fde047" stopOpacity="0" />
              <stop offset="50%" stopColor="#fde047" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#fde047" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        <ScopeRail
          active={active}
          syncOffsetMs={syncOffsetMs}
          onSyncChange={onSyncChange}
        />

        <div className="flex-1 min-w-0 relative">
          <div className="absolute inset-0 overflow-y-auto overflow-x-hidden perf-strip-scroll">
            {hasContent ? (
              <div>
                {voiceOrder.map((voice) => (
                  <VoiceTrace
                    key={voice}
                    voice={voice}
                    refs={refs}
                    nowRef={nowRef}
                    lookbehind={LOOKBEHIND}
                    lookahead={LOOKAHEAD}
                    height={TRACE_HEIGHT}
                  />
                ))}
              </div>
            ) : (
              <EmptyState />
            )}
            <ScanlineOverlay />
          </div>
        </div>

        {/* Phosphor tracer — yellow signal-bar at the bottom edge that
            pulses while transmitting. CRT scope tell. */}
        <span
          aria-hidden="true"
          data-active={active || undefined}
          className="perf-strip-tracer pointer-events-none absolute bottom-0 left-0 right-0 h-[1px]"
        />
      </section>
    </div>
  );
}

function ScopeRail({
  active,
  syncOffsetMs,
  onSyncChange,
}: {
  active: boolean;
  syncOffsetMs: number;
  onSyncChange: (n: number) => void;
}) {
  return (
    <aside
      aria-label="Scope"
      // Hidden on phones — there's no room for it next to the voice
      // rails on a 390px viewport, and the master spectrum is a
      // nice-to-have rather than essential for performance. Voice rails
      // get the full mobile width.
      className="hidden sm:flex w-[200px] md:w-[220px] shrink-0 border-r border-zinc-900 bg-gradient-to-b from-zinc-900/80 to-zinc-950 flex-col"
    >
      <div className="flex items-center gap-2 px-3 pt-2 pb-1.5 border-b border-zinc-900/70">
        <span
          aria-hidden="true"
          className={
            'h-1 w-1 rounded-full transition-colors ' +
            (active
              ? 'bg-emerald-300 shadow-[0_0_6px_rgba(134,239,172,0.6)]'
              : 'bg-zinc-700')
          }
        />
        <span className="text-[8px] tracking-[0.34em] uppercase text-zinc-500 font-display leading-none">
          scope
        </span>
        <span
          className={
            'ml-auto text-[7px] tracking-[0.3em] uppercase font-display leading-none ' +
            (active ? 'text-emerald-300' : 'text-zinc-600')
          }
        >
          {active ? 'tx' : '··'}
        </span>
      </div>

      <div className="flex-1 flex items-center justify-center px-2 py-1 min-h-0">
        <Spectrum active={active} width={160} height={36} />
      </div>

      <div className="px-3 py-1.5 border-t border-zinc-900/70 flex items-center justify-end">
        <SyncOffset value={syncOffsetMs} onChange={onSyncChange} />
      </div>
    </aside>
  );
}

function EmptyState() {
  return (
    <div className="h-full flex items-center justify-center text-center px-6">
      <p className="text-[10px] tracking-[0.28em] uppercase text-zinc-500 font-display">
        awaiting haps
      </p>
    </div>
  );
}

function ScanlineOverlay() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 opacity-[0.035] mix-blend-screen"
      style={{
        backgroundImage:
          'repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0 1px, transparent 1px 3px)',
      }}
    />
  );
}
